import type { UserAccountRole, UserAccountRoleType } from '@/client/types/admin/users-orgs';
import { getAsset } from '@/client/utils/assets';

/** `user_account.photo` is an asset object (`uuid_uc` / path), a bare id, or a URL. */
export function userPhotoUrl(photo: unknown): string | undefined {
  if (!photo) return undefined;
  if (typeof photo === 'string') {
    if (/^https?:\/\//i.test(photo)) return photo;
    return getAsset({ uuid_uc: photo } as never);
  }
  if (typeof photo === 'object') return getAsset(photo as never);
  return undefined;
}

/** How many role badges to render before collapsing the rest into a "+N" chip. */
export const MAX_ROLE_BADGES = 3;

export const roleLabel = (
  role: UserAccountRole,
  roleTypeById: Map<number, UserAccountRoleType>,
) => {
  const rt = role.user_account_role_type ?? roleTypeById.get(role.uartid);
  return rt?.description ?? rt?.name ?? `#${role.uartid}`;
};

export const genderLabel = (gender?: string | null) => {
  if (!gender) return '—';
  const g = gender.trim().toLowerCase();
  if (g === 'm' || g === 'male') return 'Male';
  if (g === 'f' || g === 'female') return 'Female';
  return gender;
};

export const roleTypeLabel = (rt: UserAccountRoleType) => rt.description ?? rt.name;

export const STATUS_BADGE: Record<string, { label: string; className: string; title: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-800', title: 'Can sign in and transact' },
  inactive: { label: 'Inactive', className: 'bg-gray-100 text-gray-700', title: 'Deactivated (active = 0)' },
  suspended: { label: 'Suspended', className: 'bg-red-100 text-red-800', title: 'admin_hold — cannot sign in or transact' },
  deleted: { label: 'Deleted', className: 'bg-gray-800 text-white', title: 'Deleted — sign-in refused' },
};

/** Same precedence as the backend's status filter. */
export const userStatus = (user: { deleted?: number | null; admin_hold?: number | null; active?: number | null }) =>
  user.deleted === 1 ? 'deleted' : user.admin_hold === 1 ? 'suspended' : user.active === 0 ? 'inactive' : 'active';
